/* eslint-disable no-magic-numbers */
import { h } from "preact";

import CSSProperties from "../../../foundation/model/CSSProperties";

import classname from "../../../foundation/utils/classname";

import styles from "./BouncingShapes.module.css";

enum ShapeColor {
    Default = "default",
    Yellow = "yellow",
    LightOrange = "lightOrange",
    Orange = "orange",
    Peach = "peach",
    Green = "green",
    Turquoise = "turquoise",
    Blue = "blue",
    Silverfish = "silverfish",
}

enum ShapeType {
    Circle = "circle",
    Square = "square",
    TriangleUp = "triangleUp",
    TriangleDown = "triangleDown",
    Pentagon = "pentagon",
}

const DEFAULT_COLORS = [
    ShapeColor.Yellow,
    ShapeColor.LightOrange,
    ShapeColor.Orange,
    ShapeColor.Peach,
    ShapeColor.Green,
    ShapeColor.Turquoise,
    ShapeColor.Blue
];

const DEFAULT_SHAPES = [
    ShapeType.Square,
    ShapeType.Circle,
    ShapeType.TriangleUp,
    ShapeType.Circle,
    ShapeType.Square,
    ShapeType.Pentagon,
    ShapeType.TriangleDown
];

const DEFAULT_SHAPE_COUNT = 7;

const shapeColorSet = new Set(Object.values(ShapeColor));

interface Props {
    class?: string;
    style?: CSSProperties;
    shapeClass?: string;
    shapeCount?: number;
    animationDuration?: number; // in milliseconds
    colors?: (ShapeColor | string)[] | string;
    shapes?: ShapeType[] | ShapeType;
}

function BouncingShapes({
    class: cssClass,
    style: propStyle,
    shapeClass,
    shapeCount = DEFAULT_SHAPE_COUNT,
    animationDuration = 180,
    colors,
    shapes
}: Props) {
    function getShapeStyle(index: number): CSSProperties {
        if (Array.isArray(colors)) {
            if (colors.length === 0) {
                return {};
            }

            const providedColor = colors[index % colors.length];
            if (shapeColorSet.has(providedColor as ShapeColor)) {
                // Use class instead of style for predefined shape colors
                return {};
            }

            return {
                color: colors[index % colors.length]
            };
        }

        // When single color is provided
        if (colors) {
            if (shapeColorSet.has(colors as ShapeColor)) {
                // Use class instead of style for predefined shape colors
                return {};
            }

            return {
                color: colors
            };
        }

        return {};
    }

    function getShapeColor(index: number): ShapeColor {
        if (Array.isArray(colors)) {
            if (colors.length === 0) {
                return ShapeColor.Default;
            }

            const providedColor = colors[index % colors.length];
            if (shapeColorSet.has(providedColor as ShapeColor)) {
                return providedColor as ShapeColor;
            }

            return ShapeColor.Default;
        }

        // When single color is provided
        if (colors) {
            if (shapeColorSet.has(colors as ShapeColor)) {
                return colors as ShapeColor;
            }

            return ShapeColor.Default;
        }

        return DEFAULT_COLORS[index % DEFAULT_COLORS.length];
    }

    function getShapeType(index: number): ShapeType {
        if (Array.isArray(shapes)) {
            if (shapes.length === 0) {
                return DEFAULT_SHAPES[index % DEFAULT_SHAPES.length];
            }

            return shapes[index % shapes.length];
        }

        // When single shape is provided
        if (shapes) {
            return shapes;
        }

        return DEFAULT_SHAPES[index % DEFAULT_SHAPES.length];
    }

    function renderShapes() {
        return Array.from({ length: shapeCount }).map((_, index) => {
            const shapeColor = styles[getShapeColor(index)];
            const shapeType = styles[getShapeType(index)];

            return (
                <div
                    key={index}
                    class={classname(styles.shape, shapeClass, shapeColor, shapeType)}
                    style={getShapeStyle(index)}/>
            );
        });
    }

    const style = {
        ...propStyle,
        "--bouncing-shapes-count": shapeCount,
        "--bouncing-shapes-animation-duration": `${animationDuration}ms`
    } as CSSProperties;

    return (
        <div class={classname(cssClass, styles.bouncingShapes)} style={style}>
            {renderShapes()}
        </div>
    );
}

export default BouncingShapes;
export {
    ShapeColor as BouncingShapeColor,
    ShapeType as BouncingShapeType
};

